import React, { useState, useEffect } from "react";
import { useSeo } from "../seo";
import { Plus, Minus } from "lucide-react";
import { DynamicNavigation, Footer } from "../components";
import CTA02 from "../components/CTA02Section";
import { useNavLinkClick } from "../hooks/useNavLinkClick";

/* ─── Visa pathway cards ─────────────────────────────────────────────────── */
const visaPathways = [
  {
    title: "Skilled Migration Visas",
    tag: "Subclass 189 / 190 / 491",
    desc: "Visas under Australia's General Skilled Migration program require a positive skills assessment before applicants can submit an Expression of Interest through SkillSelect.",
    url: null,
  },
  {
    title: "Employer-Sponsored Visas",
    tag: "Subclass 186 / 482 / 494",
    desc: "Some employer-sponsored visa pathways require applicants to demonstrate that their qualifications and professional experience meet Australian occupational standards.",
    url: null,
  },
  {
    title: "Temporary Skill Shortage Visa",
    tag: "Subclass 482",
    desc: "Certain occupations under the Temporary Skill Shortage Visa require applicants to complete a skills assessment as part of the eligibility and nomination process.",
    url: null,
  },
  {
    title: "Temporary Graduate Visa",
    tag: "Subclass 485",
    desc: "Some graduates applying for a Temporary Graduate Visa may also require a skills assessment depending on their chosen occupation and stream.",
    url: null,
  },
];

type PathwayCardProps = { title: string; tag: string; desc: string; url: string | null };

const PathwayCard = ({ title, tag, desc, url }: PathwayCardProps) => (
  <div className="group relative bg-white rounded-2xl border border-slate-200 hover:border-indigo-300 hover:shadow-xl transition-all duration-300 flex flex-col overflow-hidden">
    {/* gradient top accent bar */}
    <div className="h-1 w-full bg-gradient-to-r from-indigo-500 via-blue-500 to-purple-400" />
    <div className="flex flex-col flex-1 p-6">
      {/* subclass badge */}
      <span className="inline-block text-xs font-semibold text-indigo-600 bg-indigo-50 border border-indigo-100 rounded-full px-3 py-1 mb-4 w-max tracking-wide">
        {tag}
      </span>
      <h3 className="text-[15px] font-bold text-gray-800 mb-3 leading-snug">{title}</h3>
      <p className="text-sm text-slate-500 leading-relaxed flex-1">{desc}</p>
      {url && (
        <a
          href={url}
          className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-indigo-600 hover:text-indigo-800 group/link w-max transition-colors"
        >
          Know More
          <svg width="13" height="12" viewBox="0 0 13 12" fill="none" className="group-hover/link:translate-x-1 transition-transform duration-200">
            <path d="M12.53 6.53a.75.75 0 0 0 0-1.06L7.757.697a.75.75 0 1 0-1.06 1.06L10.939 6l-4.242 4.243a.75.75 0 0 0 1.06 1.06zM0 6v.75h12v-1.5H0z" fill="currentColor" />
          </svg>
        </a>
      )}
    </div>
  </div>
);

/* ─── Why Required items ────────────────────────────────────────────────── */
const whyItems = [
  "A positive skills assessment confirms that your qualifications and work experience meet the standards expected for your nominated occupation in Australia.",
  "For most skilled migration visa pathways, a valid assessment is mandatory before an Expression of Interest can be submitted through SkillSelect.",
  "Without a current and valid assessment, applicants generally cannot receive an Invitation to Apply through the SkillSelect system.",
  "Each occupation is assessed by a specific authority with its own documentation standards and processing timelines.",
  "Preparing the correct documents early helps avoid delays and complications during the migration process.",
  "Skills assessments protect both migrants and employers by ensuring qualifications are independently verified against Australian industry standards.",
];

/* ─── Process steps ─────────────────────────────────────────────────────── */
const processSteps = [
  {
    step: "01",
    title: "Identify the Assessing Authority",
    desc: "Every occupation listed for skilled migration is assigned to a specific assessing authority. The first step is identifying which organisation is responsible for evaluating your nominated occupation. Each authority publishes its own guidelines covering eligibility requirements, application procedures, and document checklists.",
    image: "/assets/skilled-visas-australia/application-process/step-01.jpg",
  },
  {
    step: "02",
    title: "Prepare and Submit Supporting Documents",
    desc: "Applicants must provide documentation that demonstrates their qualifications and work experience. This typically includes degree certificates and academic transcripts, employment reference letters, a professional resume or CV, identification documents, and professional registrations where applicable. The assessing authority will review these documents to determine whether the applicant meets the Australian standards for the nominated occupation.",
    image: "/assets/skilled-visas-australia/application-process/step-02.jpg",
  },
  {
    step: "03",
    title: "Receive Your Assessment Result",
    desc: "Once the review process is completed, the authority will issue an official assessment outcome. A positive result confirms that your qualifications and experience match the requirements of your nominated occupation. This assessment must then be included when submitting your visa application.",
    image: "/assets/skilled-visas-australia/application-process/step-03.jpg",
  },
];

/* ─── FAQ data ───────────────────────────────────────────────────────────── */
const faqs = [
  {
    q: "What is a skills assessment for Australia?",
    a: "A skills assessment is a formal review carried out by an Australian assessing authority to verify whether your qualifications and professional experience meet the requirements of a specific occupation recognised in Australia's skilled migration framework.",
  },
  {
    q: "Is a skills assessment mandatory for skilled migration?",
    a: "For most skilled migration visa pathways, including the Subclass 189 and Subclass 190, a positive skills assessment is mandatory before applicants can submit or receive an invitation through SkillSelect.",
  },
  {
    q: "Who carries out skills assessments in Australia?",
    a: "Skills assessments are conducted by designated assessing authorities. Each occupation on Australia's Skilled Occupation List is linked to a specific authority responsible for evaluating applicants in that field.",
  },
  {
    q: "How long does a skills assessment take?",
    a: "Processing times vary by assessing authority, occupation, and the completeness of submitted documents. Some assessments are completed within weeks, while others may take several months. Check directly with the relevant authority for current timeframes.",
  },
  {
    q: "What documents are typically required?",
    a: "Most assessing authorities require degree certificates, academic transcripts, employment reference letters, a professional resume or CV, identification documents, and professional registrations where applicable. Requirements vary by occupation and authority.",
  },
  {
    q: "Can I apply for a visa without a positive skills assessment?",
    a: "In most cases, no. A positive skills assessment from the relevant authority is a prerequisite for most skilled migration visa pathways, including lodging an Expression of Interest through SkillSelect.",
  },
];

/* ─── Page ───────────────────────────────────────────────────────────────── */
export default function AustraliaSkillsAssessment() {
  const [openFaqIndex, setOpenFaqIndex] = useState(0);
  const [activeProcess, setActiveProcess] = useState(0);
  const [prevProcess, setPrevProcess] = useState<number | null>(null);
  const [slideDir, setSlideDir] = useState<"next" | "prev">("next");
  const [imgAnimating, setImgAnimating] = useState(false);

  const navigateProcess = (dir: "next" | "prev") => {
    if (imgAnimating) return;
    const nextIdx =
      dir === "next"
        ? (activeProcess + 1) % processSteps.length
        : (activeProcess - 1 + processSteps.length) % processSteps.length;
    setSlideDir(dir);
    setPrevProcess(activeProcess);
    setActiveProcess(nextIdx);
    setImgAnimating(true);
    setTimeout(() => { setPrevProcess(null); setImgAnimating(false); }, 540);
  };

  const goToProcess = (idx: number) => {
    if (idx === activeProcess || imgAnimating) return;
    const dir = idx > activeProcess ? "next" : "prev";
    setSlideDir(dir);
    setPrevProcess(activeProcess);
    setActiveProcess(idx);
    setImgAnimating(true);
    setTimeout(() => { setPrevProcess(null); setImgAnimating(false); }, 540);
  };

  useEffect(() => {
    const timer = setInterval(() => navigateProcess("next"), 3000);
    return () => clearInterval(timer);
  }, [activeProcess, imgAnimating]);

  // Guard against out-of-bounds index (can occur after HMR when step count changes)
  const safeIdx = activeProcess < processSteps.length ? activeProcess : 0;

  const links = [
    { id: "home", label: "Home", href: "/" },
    { id: "services", label: "Services", href: "/services" },
    { id: "about", label: "About", href: "/about" },
    { id: "contact", label: "Contact", href: "/contact" },
  ];

  const onLinkClick = useNavLinkClick(links);

  useSeo({
    title: "Skills Assessment for Australia | Skilled Migration Requirements",
    description:
      "Understand how Australia's skills assessment process works, which assessing authority reviews your occupation, and how a positive assessment supports your skilled visa application.",
    path: "/australia-skills-assessment",
    breadcrumbs: [
      { name: "Home", url: "/" },
      { name: "Skills Assessment Australia", url: "/australia-skills-assessment" },
    ],
    faqItems: faqs.map((f) => ({ question: f.q, answer: f.a })),
  });

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      <header className="w-full">
        <DynamicNavigation
          links={links}
          logo={<img src="/logo.png" alt="Logo" className="h-10 w-auto" />}
          showLabelsOnMobile={true}
          glowIntensity={6}
          onLinkClick={onLinkClick}
        />
      </header>

      <main className="w-full flex flex-col items-center max-w-full">

        {/* HERO */}
        <section className="bg-gradient-to-b from-[#F5F7FF] via-[#fffbee] to-[#E6EFFF] w-full font-poppins">
          <div className="flex flex-col md:flex-row items-center max-md:text-center justify-between mt-24 pb-16 px-6 sm:px-10 md:px-24 max-w-7xl mx-auto w-full">
            <div className="flex flex-col items-center md:items-start">
              <button
                type="button"
                className="mt-16 mb-6 flex items-center space-x-2 border border-indigo-600 text-indigo-600 text-xs rounded-full px-4 py-1.5 hover:bg-indigo-50 transition"
              >
                <span>A key step in Australia's skilled migration process.</span>
              </button>
              <h1 className="text-gray-900 font-semibold text-4xl sm:text-5xl md:text-6xl max-w-xl" style={{ fontFamily: "Poppins, sans-serif" }}>
                Skills Assessment{" "}
                <span className="text-indigo-600">Australia</span>
              </h1>
              <p className="mt-4 text-gray-600 max-w-md text-sm sm:text-base leading-relaxed">
                Discover if your qualifications and work experience make you a strong candidate for skilled migration to Australia.
              </p>

            </div>
            <div aria-label="Skills assessment images" className="mt-12 grid grid-cols-2 gap-6 pb-6">
              <img alt="Skills Assessment" src="/assets/services-cards/images/skills-assessment.jpg" className="w-36 h-44 rounded-lg hover:scale-105 transition duration-300 object-cover flex-shrink-0 shadow-lg" />
              <img alt="Skilled Visas" src="/assets/services-cards/images/skilled-visas.jpg" className="w-36 h-44 rounded-lg hover:scale-105 transition duration-300 object-cover flex-shrink-0 shadow-lg" />
              <img alt="Employer Sponsored" src="/assets/services-cards/images/employer-sponsored-visas.jpg" className="w-36 h-44 rounded-lg hover:scale-105 transition duration-300 object-cover flex-shrink-0 shadow-lg" />
              <img alt="Corporate Immigration" src="/assets/services-cards/images/corporate-immigration.jpg" className="w-36 h-44 rounded-lg hover:scale-105 transition duration-300 object-cover flex-shrink-0 shadow-lg" />
            </div>
          </div>
        </section>

        {/* WHAT IS A SKILLS ASSESSMENT */}
        <section className="w-full bg-white py-12 sm:py-16 px-4 sm:px-6 md:px-10 lg:px-24">
          <div className="w-full max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-center gap-8 md:gap-12 lg:gap-16">
            <div className="relative rounded-2xl overflow-hidden shrink-0 w-full max-w-xs sm:max-w-sm md:max-w-md group">
              <img
                className="w-full object-cover rounded-2xl aspect-square transition-transform duration-500 ease-in-out group-hover:scale-110"
                src="/assets/skilled-visas-australia/what-is-skilled-visa/image.jpg"
                alt="What is a Skills Assessment for Australia"
              />
            </div>
            <div className="text-sm text-slate-600 w-full max-w-lg">
              <h2 className="text-2xl sm:text-3xl md:text-4xl uppercase font-semibold text-slate-700" style={{ fontFamily: "Poppins, sans-serif" }}>
                What Is a Skills Assessment?
              </h2>
              <div className="w-24 h-[3px] rounded-full bg-gradient-to-r from-indigo-600 to-[#DDD9FF] mt-2 mb-6 sm:mb-8"></div>
              <p className="text-sm sm:text-base text-[#333] leading-[1.7]">
                A <strong>skills assessment</strong> is a formal review carried out by an authorised Australian assessing authority. Its purpose is to verify whether your qualifications and professional experience align with the requirements of a specific occupation recognised in Australia.
              </p>
              <p className="mt-4 text-sm sm:text-base text-[#333] leading-[1.7]">
                For skilled migration, applicants must nominate an occupation from the Australian Skilled Occupation List. Each occupation is linked to a designated organisation responsible for assessing applicants in that field. These authorities review your academic background, employment history, and supporting documents to determine whether your profile meets Australian industry standards.
              </p>
              <p className="mt-4 text-sm sm:text-base text-[#333] leading-[1.7]">
                Receiving a <strong>positive assessment</strong> confirms that your skills are considered suitable for the occupation you intend to nominate in your migration application.
              </p>
            </div>
          </div>
        </section>

        {/* VISA PATHWAYS CARDS */}
        <section className="w-full bg-blue-50 py-12 sm:py-16 px-4 sm:px-6 md:px-10 lg:px-24">
          <div className="w-full max-w-6xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-semibold text-[#0a2540] mb-3" style={{ fontFamily: "Poppins, sans-serif" }}>
              Visa Pathways That May Require a Skills Assessment
            </h2>
            <div className="w-24 h-[3px] rounded-full bg-gradient-to-r from-indigo-600 to-[#DDD9FF] mb-5"></div>
            <p className="text-base text-slate-500 mb-3 max-w-3xl">
              Skills assessments are commonly required for several Australian visa categories, particularly those designed for skilled professionals.
            </p>
            <p className="text-sm text-slate-400 mb-10">Below are the main visa pathways where a skills assessment may be required.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {visaPathways.map((p, i) => (
                <PathwayCard key={i} {...p} />
              ))}
            </div>
          </div>
        </section>

        {/* WHY IS A SKILLS ASSESSMENT REQUIRED */}
        <section className="w-full bg-gray-50 py-16 px-6 sm:px-10 md:px-24">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-semibold text-[#0a2540] mb-4" style={{ fontFamily: "Poppins, sans-serif" }}>
              Why is a Skills Assessment Required?
            </h2>
            <div className="w-24 h-[3px] rounded-full bg-gradient-to-r from-indigo-600 to-[#DDD9FF] mb-7"></div>
            <p className="text-base sm:text-lg text-slate-600 leading-relaxed mb-5">
              Skills assessments are an important part of Australia's migration system. They help ensure that migrants entering the Australian workforce have the qualifications, skills, and work experience required for their nominated occupation.
            </p>
            <p className="text-base sm:text-lg text-slate-600 leading-relaxed mb-5">
              For many skilled migration visa pathways, a positive skills assessment from the relevant assessing authority is mandatory. Without a valid skills assessment, applicants generally cannot proceed with their skilled visa application, including submitting or receiving an invitation through the SkillSelect system.
            </p>
            <p className="text-base sm:text-lg text-slate-600 leading-relaxed">
              Each occupation is assessed by a specific assessing authority, and every authority has its own requirements, documentation standards, and processing timelines. Preparing the correct documents early can help avoid delays and complications during the migration process.
            </p>
          </div>
        </section>

        {/* STEPS */}
        <section className="w-full relative overflow-hidden" style={{ background: "#1B31F5" }}>
          <div className="max-w-6xl mx-auto px-6 sm:px-10 md:px-16 pt-16 pb-0 relative">
            <div
              aria-hidden="true"
              className="absolute inset-0 flex items-center justify-end pr-4 sm:pr-20 pointer-events-none select-none z-0 overflow-hidden"
            >
              <span
                key={"num-" + safeIdx}
                className="animate-process-num text-[320px] sm:text-[420px] md:text-[520px] font-black leading-none"
                style={{ color: "rgba(255,255,255,0.13)", letterSpacing: "-0.04em" }}
              >
                {processSteps[safeIdx].step}
              </span>
            </div>
            <h2
              className="relative z-10 text-white font-bold text-3xl sm:text-4xl md:text-5xl text-center mb-12"
              style={{ fontFamily: "Poppins, sans-serif" }}
            >
              Steps in the Skills Assessment Process
            </h2>
            <div className="relative z-10 flex flex-col lg:flex-row items-center justify-between gap-12">
              <div
                key={safeIdx}
                className={"flex-1 max-w-lg " + (prevProcess !== null ? (slideDir === "next" ? "proc-step-enter-right" : "proc-step-enter-left") : "")}
              >
                <p className="text-white/50 text-xs uppercase tracking-[0.2em] font-semibold mb-3">
                  Step {processSteps[safeIdx].step}
                </p>
                <h3 className="text-white font-bold text-3xl sm:text-4xl mb-4 leading-snug">
                  {processSteps[safeIdx].title}
                </h3>
                <p className="text-white/80 text-base sm:text-lg leading-relaxed mb-10">
                  {processSteps[safeIdx].desc}
                </p>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => navigateProcess("prev")}
                    className="w-12 h-12 rounded-full bg-white/10 hover:bg-white/25 border border-white/25 flex items-center justify-center transition"
                    aria-label="Previous step"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path d="M15 18l-6-6 6-6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                  <button
                    onClick={() => navigateProcess("next")}
                    className="w-12 h-12 rounded-full bg-white/10 hover:bg-white/25 border border-white/25 flex items-center justify-center transition"
                    aria-label="Next step"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path d="M9 18l6-6-6-6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                </div>
              </div>
              <div className="flex-shrink-0 flex items-end justify-center lg:justify-end mb-14">
                <div className="relative w-80 h-64 sm:w-[440px] sm:h-80">
                  {prevProcess !== null && prevProcess < processSteps.length && (
                    <img
                      src={processSteps[prevProcess].image}
                      alt=""
                      aria-hidden="true"
                      className={"absolute inset-0 w-full h-full object-cover rounded-2xl shadow-2xl shadow-black/40 " + (slideDir === "next" ? "proc-img-exit-left" : "proc-img-exit-right")}
                    />
                  )}
                  <img
                    key={safeIdx}
                    src={processSteps[safeIdx].image}
                    alt={processSteps[safeIdx].title}
                    className={"absolute inset-0 w-full h-full object-cover rounded-2xl shadow-2xl shadow-black/40 " + (prevProcess !== null ? (slideDir === "next" ? "proc-img-enter-right" : "proc-img-enter-left") : "")}
                  />
                </div>
              </div>
            </div>
          </div>
          {/* Timeline */}
          <div className="relative z-10 max-w-6xl mx-auto px-6 sm:px-10 md:px-16">
            <div className="flex items-start gap-0 overflow-x-auto pb-12 scrollbar-hide">
              {processSteps.map((s, i) => (
                <button
                  key={i}
                  onClick={() => goToProcess(i)}
                  className={"flex flex-col items-start flex-1 min-w-[90px] pr-3 transition-opacity duration-300 " + (safeIdx === i ? "opacity-100" : "opacity-40 hover:opacity-65")}
                >
                  <div className="relative w-full flex items-center mb-4" style={{ height: "14px" }}>
                    <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-px bg-white/25" />
                    <span className={"relative z-10 block rounded-full border-2 transition-all duration-300 shrink-0 " + (safeIdx === i ? "w-3.5 h-3.5 bg-white border-white shadow-[0_0_8px_2px_rgba(255,255,255,0.45)]" : "w-2 h-2 bg-white/30 border-white/40")} />
                  </div>
                  <span className={"font-bold text-base sm:text-lg leading-none " + (safeIdx === i ? "text-white" : "text-white/70")}>
                    {s.step}
                  </span>
                  <span className={"text-xs mt-1.5 leading-snug text-left " + (safeIdx === i ? "text-white font-semibold" : "text-white/55")}>
                    {s.title}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* HOW HOLA HELPS */}
        <section className="w-full bg-zinc-100 py-24 px-4">
          <div className="max-w-6xl mx-auto">
            <div className="mb-12">
              <h2 className="text-4xl md:text-5xl font-semibold text-neutral-800" style={{ fontFamily: "Poppins, sans-serif" }}>
                How Hola World Migration Can Help
              </h2>
              <div className="w-24 h-[3px] rounded-full bg-gradient-to-r from-indigo-600 to-[#DDD9FF] mt-3 mb-6"></div>
              <p className="text-base text-neutral-600 max-w-2xl mt-2 leading-relaxed">
                Navigating the skills assessment process can sometimes be confusing, particularly when selecting the correct occupation or preparing documentation.
              </p>
              <p className="text-base text-neutral-600 max-w-2xl mt-4 leading-relaxed">
                At Hola World Migration, our team assists applicants in identifying suitable migration pathways and preparing their skills assessment applications with the required documentation. With the right preparation and guidance, the process can become much smoother and more manageable.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-[320px_1fr_1fr] gap-6">
              <div className="md:row-span-2 relative rounded-2xl overflow-hidden bg-indigo-600 p-8 flex flex-col justify-between min-h-[360px]">
                <div aria-hidden="true" className="absolute -bottom-4 -right-3 text-[150px] font-black leading-none text-white/10 select-none pointer-events-none">AU</div>
                <div>
                  <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center mb-6">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                      <path d="M9 11l3 3L22 4" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <h3 className="text-white font-semibold text-2xl mb-3 leading-snug">Expert guidance through the assessment process</h3>
                  <p className="text-white/70 text-sm leading-relaxed mt-3">
                    At Hola World Migration, our team helps applicants identify the right migration pathways and prepare their skills assessment applications with the correct documentation.
                  </p>
                </div>
              </div>
              {[
                {
                  text: "Occupation identification and nominated occupation guidance",
                  icon: (
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                      <circle cx="11" cy="11" r="8" stroke="#4F46E5" strokeWidth="1.8"/><path d="M21 21l-4.35-4.35" stroke="#4F46E5" strokeWidth="1.8" strokeLinecap="round"/>
                    </svg>
                  ),
                },
                {
                  text: "Identifying the correct assessing authority for your occupation",
                  icon: (
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" stroke="#4F46E5" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  ),
                },
                {
                  text: "Document preparation and compilation support",
                  icon: (
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="#4F46E5" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" stroke="#4F46E5" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  ),
                },
                {
                  text: "Migration pathway planning based on your full profile",
                  icon: (
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                      <path d="M22 12h-4l-3 9L9 3l-3 9H2" stroke="#4F46E5" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  ),
                },
              ].map((item, i) => (
                <div key={i} className="bg-white rounded-2xl hover:shadow-md transition-all duration-300 p-7 flex flex-col justify-between min-h-[160px]">
                  <div>
                    <div className="w-12 h-12 rounded-xl bg-indigo-50 flex items-center justify-center mb-5">
                      {item.icon}
                    </div>
                    <p className="text-base font-medium text-neutral-700 leading-[1.6]">{item.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="w-full bg-gray-50 py-24 px-4">
          <div className="w-full max-w-5xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
              <div className="lg:sticky lg:top-24">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 border border-blue-200 mb-6">
                  <span className="text-xs font-bold uppercase tracking-wider text-blue-600">Self Service Help</span>
                </div>
                <h2 className="text-4xl md:text-5xl font-black text-[#0a2540] mb-8" style={{ lineHeight: "1.1" }}>
                  Frequently Asked <br /> Questions
                </h2>
                <p className="text-base text-gray-600 max-w-sm mb-6 leading-relaxed">
                  Have questions about skills assessments? Find answers to the most common questions below.
                </p>
                <div className="rounded-2xl overflow-hidden w-full max-w-sm">
                  <img
                    src="/assets/skilled-visas-australia/faq/faq-image.png"
                    alt="Skills Assessment FAQ"
                    className="w-full h-auto object-contain"
                  />
                </div>
              </div>
              <div className="space-y-4">
                {faqs.map((faq, i) => (
                  <div
                    key={i}
                    className="rounded-xl border border-gray-200 bg-white hover:border-blue-300 transition-colors"
                  >
                    <button
                      onClick={() => setOpenFaqIndex(openFaqIndex === i ? -1 : i)}
                      className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition-colors"
                    >
                      <span className="text-base sm:text-lg font-semibold text-[#0a2540] pr-4">{faq.q}</span>
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 bg-gray-100">
                        {openFaqIndex === i ? (
                          <Minus className="w-4 h-4 text-gray-600" />
                        ) : (
                          <Plus className="w-4 h-4 text-gray-600" />
                        )}
                      </div>
                    </button>
                    {openFaqIndex === i && (
                      <div className="px-6 pb-6">
                        <div className="h-px bg-gray-200 mb-6" />
                        <p className="text-base text-gray-700 leading-relaxed">{faq.a}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <CTA02 />
      </main>

      <div className="py-8 px-6 md:px-24 bg-white border-t border-gray-100">
        <p className="text-xs text-gray-400 leading-relaxed max-w-5xl mx-auto">
          <strong className="text-gray-500">Disclaimer:</strong> The information on this page is intended as general guidance only and does not constitute legal, migration, or professional advice. Skills assessment requirements, processing times, and occupation lists are subject to change by the relevant assessing authorities and the Australian Government without notice. Always verify current requirements directly with the relevant assessing authority or consult a registered migration agent (MARA) before making decisions about your migration pathway.
        </p>
      </div>

      <Footer />
    </div>
  );
}
